/**
 * Content Studio — the weekly engine's output surface. Briefs, produced
 * assets with committee verdicts, and the posting queue. Publication is
 * manual v1: copy an approved body, post it on the channel, paste the live
 * URL and confirm — the measurement loop reads it from channel_post later.
 */
import { useState } from "react";
import { Chip, Empty, ErrorNote, Panel, useApi } from "../components/ui.js";
import { api } from "../lib/api.js";
import { fmtDate } from "../lib/format.js";

interface BriefRow {
  id: string; week_of: string; title: string; topic: string; angle: string;
  funnel_stage: string; target_channel: string; persona_voice: string; status: string;
}
interface AssetRow {
  id: string; asset_type: string; channel: string; title: string; body: string;
  qa_flags: string[] | null; status: string; created_at: string;
  committee_score: number | null; committee_outcome: string | null;
  vetoed_by_domains: string[] | null; consolidated_changes: string[] | null;
}
interface QueueRow {
  id: string; content_asset_id: string; channel: string; scheduled_at: string | null;
  published_at: string | null; external_post_url: string | null; status: string;
  title: string; asset_type: string; body: string;
}

function outcomeTone(outcome: string | null): "green" | "amber" | undefined {
  if (outcome === "approved" || outcome === "approved_with_notes") return "green";
  if (outcome === "escalated" || outcome === "revision_required") return "amber";
  return undefined;
}

export function Studio() {
  const data = useApi<{ briefs: BriefRow[]; assets: AssetRow[]; queue: QueueRow[] }>("/api/studio");
  const [openAssetId, setOpenAssetId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [urlDrafts, setUrlDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);
  const [testMode, setTestMode] = useState(true);
  const [weekOf, setWeekOf] = useState("");

  const openAsset = data.data?.assets.find((a) => a.id === openAssetId) ?? null;

  // Fire a content-engine tick on demand (force = run now, outside the Friday
  // cron). Proxied through dashboard-api → orchestrator with the internal token
  // the API already holds; no token handling here.
  async function runEngine() {
    setRunning(true);
    setRunResult(null);
    try {
      const body: Record<string, unknown> = { force: true };
      if (testMode) { body.maxBriefs = 1; body.maxAssets = 1; }
      if (weekOf.trim()) body.weekOf = weekOf.trim();
      const r = await api.post<{ ran: boolean; briefs: number; assets: number; queuedForApproval: number; reason?: string }>(
        "/api/studio/run-engine", body,
      );
      setRunResult(
        r.ran
          ? `✓ produced ${r.briefs} brief / ${r.assets} post(s) · ${r.queuedForApproval} queued in Approvals`
          : `did not run: ${r.reason ?? "unknown"}`,
      );
      data.reload();
    } catch (e) {
      setRunResult(`failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setRunning(false);
    }
  }

  async function copyBody(id: string, body: string) {
    await navigator.clipboard.writeText(body);
    setCopiedId(id);
  }

  async function markPublished(post: QueueRow) {
    setBusy(true);
    try {
      await api.post(`/api/studio/posts/${post.id}/published`, {
        url: (urlDrafts[post.id] ?? "").trim() || undefined,
      });
      data.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Studio</h1>
        <span className="stamp">weekly engine output · committee advises, you decide · posting is manual for now</span>
      </div>

      {data.error ? <ErrorNote error={data.error} /> : null}

      <Panel title="Run the content engine on demand" delay={0}>
        <div className="row" style={{ flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <button className="btn primary" disabled={running} onClick={() => void runEngine()}>
            {running ? "Running… (gpt-5.5-pro, up to ~2 min)" : "Run content engine now"}
          </button>
          <label className="dim" style={{ fontSize: 13, display: "flex", gap: 5, alignItems: "center" }}>
            <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} />
            cheap test (1 brief / 1 post)
          </label>
          <input
            style={{ width: 200 }}
            placeholder="week (YYYY-MM-DD, optional)"
            value={weekOf}
            onChange={(e) => setWeekOf(e.target.value)}
          />
          {runResult ? (
            <span className="tag" style={{ color: runResult.startsWith("✓") ? "var(--green)" : "var(--amber)" }}>{runResult}</span>
          ) : null}
        </div>
        <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
          Forces a run now, outside the Friday cron. Produced posts land in Approvals for your review.
          Generation runs on gpt-5.5-pro and bills per post; a week that already has briefs is skipped.
        </div>
      </Panel>

      <Panel title="Posting queue — approved, waiting to go live" delay={60}>
        {(data.data?.queue ?? []).filter((q) => q.status === "scheduled").length === 0 ? (
          <Empty>nothing approved and unposted — approve content in Approvals and it lands here</Empty>
        ) : (
          (data.data?.queue ?? []).filter((q) => q.status === "scheduled").map((q) => (
            <div key={q.id} style={{ marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid var(--line)" }}>
              <div className="row" style={{ marginBottom: 6 }}>
                <Chip tone="blue">{q.channel}</Chip>
                <span className="dim">{q.asset_type}</span>
                <strong>{q.title}</strong>
                <span className="spacer" />
                <button className="btn sm" onClick={() => void copyBody(q.id, q.body)}>
                  {copiedId === q.id ? "✓ copied" : "Copy post"}
                </button>
              </div>
              <pre className="mono" style={{ fontSize: 14, whiteSpace: "pre-wrap", maxHeight: 160, overflow: "auto", margin: "6px 0" }}>
                {q.body}
              </pre>
              <div className="row">
                <input
                  style={{ flex: 1 }}
                  placeholder="paste the live post URL after publishing (optional)"
                  value={urlDrafts[q.id] ?? ""}
                  onChange={(e) => setUrlDrafts({ ...urlDrafts, [q.id]: e.target.value })}
                />
                <button className="btn sm ghost-green" disabled={busy} onClick={() => void markPublished(q)}>
                  Mark published
                </button>
              </div>
            </div>
          ))
        )}
      </Panel>

      <div className="grid cols-2" style={{ marginTop: 14 }}>
        <Panel title="Assets — committee verdicts" delay={60}>
          {(data.data?.assets ?? []).length === 0 ? (
            <Empty>no assets yet — the engine runs Fridays once enabled (Approvals #4)</Empty>
          ) : (
            <table className="data">
              <thead><tr><th>asset</th><th>channel</th><th>committee</th><th>status</th></tr></thead>
              <tbody>
                {(data.data?.assets ?? []).map((a) => (
                  <tr key={a.id} className="clickable" onClick={() => setOpenAssetId(a.id)}
                      style={openAssetId === a.id ? { outline: "1px solid var(--red-dim)" } : undefined}>
                    <td>{a.title}<div className="faint" style={{ fontSize: 12 }}>{a.asset_type}</div></td>
                    <td className="dim">{a.channel}</td>
                    <td>
                      {a.committee_score != null ? (
                        <Chip tone={outcomeTone(a.committee_outcome)}>
                          {Math.round(a.committee_score)} · {a.committee_outcome ?? "?"}
                        </Chip>
                      ) : <span className="faint">—</span>}
                      {(a.qa_flags ?? []).length > 0 ? <Chip tone="amber">flagged</Chip> : null}
                    </td>
                    <td className="dim">{a.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title={openAsset ? `asset #${openAsset.id}` : "detail"} delay={120}>
          {!openAsset ? <Empty>select an asset</Empty> : (
            <>
              <div className="tag" style={{ marginBottom: 8 }}>
                {openAsset.asset_type} · {openAsset.channel}
                {openAsset.committee_score != null ? ` · committee ${Math.round(openAsset.committee_score)}/100 (${openAsset.committee_outcome})` : ""}
              </div>
              {(openAsset.vetoed_by_domains ?? []).length > 0 ? (
                <div className="tag" style={{ color: "var(--red)" }}>vetoed by: {(openAsset.vetoed_by_domains ?? []).join(", ")}</div>
              ) : null}
              {(openAsset.qa_flags ?? []).length > 0 ? (
                <div className="tag" style={{ color: "var(--amber)" }}>{(openAsset.qa_flags ?? []).join(" · ")}</div>
              ) : null}
              <pre className="mono" style={{ fontSize: 14, whiteSpace: "pre-wrap", maxHeight: 300, overflow: "auto" }}>
                {openAsset.body}
              </pre>
              {(openAsset.consolidated_changes ?? []).length > 0 ? (
                <>
                  <div className="tag">committee change requests</div>
                  <ul style={{ fontSize: 14, margin: "6px 0 0 16px" }}>
                    {(openAsset.consolidated_changes ?? []).map((ch, i) => <li key={i} className="dim">{ch}</li>)}
                  </ul>
                </>
              ) : null}
            </>
          )}
        </Panel>
      </div>

      <Panel title="Briefs" delay={180}>
        {(data.data?.briefs ?? []).length === 0 ? (
          <Empty>no briefs yet</Empty>
        ) : (
          <table className="data">
            <thead><tr><th>week of</th><th>title</th><th>topic</th><th>stage</th><th>channel</th><th>voice</th><th>status</th></tr></thead>
            <tbody>
              {(data.data?.briefs ?? []).map((b) => (
                <tr key={b.id}>
                  <td className="num dim">{fmtDate(b.week_of)}</td>
                  <td>{b.title}</td>
                  <td className="dim">{b.topic}</td>
                  <td className="dim">{b.funnel_stage}</td>
                  <td><Chip>{b.target_channel}</Chip></td>
                  <td className="dim">{b.persona_voice}</td>
                  <td className="dim">{b.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}
